import { Injectable } from '@angular/core';
import { ProductInterface, MediaInterface } from './http/apiservicemodel';
import { environment } from '../../environments/environment';

// Interface para as imagens no formato da API
interface ProductImageInterface {
  id: number;
  url: string;
  is_main: boolean;
  created_at?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ImageService {

  private readonly API_BASE_URL = environment.apiUrl;

  // Imagens de fallback organizadas por categoria
  private readonly fallbackImages = {
    atracao: [
      '/hero.jpg',
      '/beach.png',
      '/atration.png'
    ],
    alojamento: [
      '/rooms.png',
      '/hero.jpg'
    ],
    restaurante: [
      '/restaurant.png',
      '/hero.jpg'
    ],
    evento: [
      '/party.png',
      '/hero.jpg'
    ],
    default: [
      '/hero.jpg',
      '/beach.png',
      '/rooms.png',
      '/restaurant.png',
      '/party.png',
      '/atration.png'
    ]
  };

  /**
   * Obtém a URL completa da imagem, tratando caminhos relativos e absolutos
   */
  private getFullImageUrl(imagePath: string): string {
    if (!imagePath) return '';

    // Se já é uma URL completa (http/https), retorna como está
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }

    // Se começa com /, é um caminho absoluto local
    if (imagePath.startsWith('/')) {
      return imagePath;
    }

    // Se começa com uploads/ ou não tem protocolo, adicionar API URL
    const baseUrl = this.API_BASE_URL.endsWith('/')
      ? this.API_BASE_URL.slice(0, -1)
      : this.API_BASE_URL;

    return `${baseUrl}/${imagePath}`;
  }

  /**
   * Obtém a imagem primária de um produto
   */
  public getPrimaryImage(product: ProductInterface): string {
    // 1. Verificar se há imagens no array de images (formato novo da API)
    if (product.images && Array.isArray(product.images) && product.images.length > 0) {
      // Se images é um array de strings (formato antigo)
      if (typeof product.images[0] === 'string') {
        return this.getFullImageUrl(product.images[0]);
      }

      // Se images é um array de objetos (formato novo)
      const images = product.images as any[];
      const mainImage = images.find(img => img.is_main === true);
      if (mainImage?.url) {
        return this.getFullImageUrl(mainImage.url);
      }

      // Se não há imagem principal, usar a primeira
      if (images[0]?.url) {
        return this.getFullImageUrl(images[0].url);
      }
    }

    // 2. Verificar o campo media (formato antigo)
    if (product.media && product.media.length > 0) {
      const primaryImage = product.media.find(media => media.is_primary === 1 && media.media_type === 'photo');
      if (primaryImage?.file_path) {
        return this.getFullImageUrl(primaryImage.file_path);
      }

      // Se não há imagem primária, usar a primeira foto
      const firstPhoto = product.media.find(media => media.media_type === 'photo');
      if (firstPhoto?.file_path) {
        return this.getFullImageUrl(firstPhoto.file_path);
      }
    }

    // 3. Verificar campo primary_image
    if (product.primary_image) {
      return this.getFullImageUrl(product.primary_image);
    }

    // 4. Fallback para imagem baseada no tipo/categoria
    return this.getFallbackImage(product);
  }

  /**
   * Obtém todas as imagens de um produto
   */
  public getProductImages(product: ProductInterface): string[] {
    const images: string[] = [];

    // 1. Verificar array images (formato novo)
    if (product.images && Array.isArray(product.images) && product.images.length > 0) {
      // Se images é um array de strings (formato antigo)
      if (typeof product.images[0] === 'string') {
        product.images.forEach(img => {
          if (typeof img === 'string') {
            images.push(this.getFullImageUrl(img));
          }
        });
      } else {
        // Se images é um array de objetos (formato novo)
        const imageObjects = product.images as any[];
        imageObjects
          .sort((a, b) => {
            // Ordenar: imagem principal primeiro, depois por ordem
            if (a.is_main && !b.is_main) return -1;
            if (!a.is_main && b.is_main) return 1;
            return 0;
          })
          .forEach(img => {
            if (img.url) {
              images.push(this.getFullImageUrl(img.url));
            }
          });
      }
    }

    // 2. Se não há imagens no formato novo, verificar media (formato antigo)
    if (images.length === 0 && product.media && product.media.length > 0) {
      product.media
        .filter(media => media.media_type === 'photo')
        .sort((a, b) => {
          // Ordenar: imagem primária primeiro, depois por display_order
          if (a.is_primary === 1 && b.is_primary !== 1) return -1;
          if (a.is_primary !== 1 && b.is_primary === 1) return 1;
          return a.display_order - b.display_order;
        })
        .forEach(media => {
          if (media.file_path) {
            images.push(this.getFullImageUrl(media.file_path));
          }
        });
    }

    // 3. Se ainda não há imagens, usar primary_image
    if (images.length === 0 && product.primary_image) {
      images.push(this.getFullImageUrl(product.primary_image));
    }

    // 4. Se não há imagens reais, retornar fallback
    if (images.length === 0) {
      images.push(this.getFallbackImage(product));
    }

    return images;
  }

  /**
   * Obtém uma imagem de fallback baseada no tipo/categoria do produto
   */
  private getFallbackImage(product: ProductInterface): string {
    const productType = product.type?.toLowerCase() || '';
    const categoryName = product.category?.name?.toLowerCase() || '';

    let fallbackCategory = 'default';

    // Determinar categoria de fallback baseada no tipo ou categoria
    if (productType.includes('atracao') || categoryName.includes('atração') || categoryName.includes('turístico')) {
      fallbackCategory = 'atracao';
    } else if (productType.includes('alojamento') || categoryName.includes('hotel') || categoryName.includes('alojamento')) {
      fallbackCategory = 'alojamento';
    } else if (productType.includes('restaurante') || categoryName.includes('restaurante') || categoryName.includes('comida')) {
      fallbackCategory = 'restaurante';
    } else if (productType.includes('evento') || categoryName.includes('evento') || categoryName.includes('festa')) {
      fallbackCategory = 'evento';
    }

    const fallbacks = this.fallbackImages[fallbackCategory as keyof typeof this.fallbackImages] || this.fallbackImages.default;

    // Usar o ID do produto para selecionar uma imagem consistente
    const index = product.id ? (product.id % fallbacks.length) : 0;
    return fallbacks[index];
  }

  /**
   * Obtém uma imagem específica por índice
   */
  public getImageByIndex(product: ProductInterface, index: number): string {
    const images = this.getProductImages(product);
    if (index >= 0 && index < images.length) {
      return images[index];
    }
    return this.getPrimaryImage(product);
  }

  /**
   * Verifica se um produto tem imagens reais (não fallback)
   */
  public hasRealImages(product: ProductInterface): boolean {
    return (
      (product.images && product.images.length > 0) ||
      (product.media && product.media.some(m => m.media_type === 'photo')) ||
      !!product.primary_image
    );
  }

  /**
   * Obtém o número total de imagens de um produto
   */
  public getImageCount(product: ProductInterface): number {
    return this.getProductImages(product).length;
  }
}
